import React, { useState } from 'react';
import { formatIndianCurrency } from '../../sales-pipeline/utils/formatters';
import { getDivisionConfig } from '../../../config/divisions';

const ApprovalCard = ({ deal, onApprove, onReject, onViewDetails }) => {
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const divisionConfig = getDivisionConfig(deal.Division);

  const handleApprove = async () => {
    if (isApproving || isRejecting) return;
    
    setIsApproving(true);
    try {
      const dealName = `${deal.AccountName} - ${deal.ServiceType}`;
      await onApprove(deal.ID, dealName);
    } catch (error) {
      console.error('Error approving deal:', error);
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    if (isApproving || isRejecting) return;
    
    setIsRejecting(true);
    try {
      const dealName = `${deal.AccountName} - ${deal.ServiceType}`;
      await onReject(deal.ID, dealName);
    } catch (error) {
      console.error('Error rejecting deal:', error);
    } finally {
      setIsRejecting(false);
    }
  };

  const handleViewDetails = () => {
    onViewDetails?.(deal);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 truncate" title={deal.AccountName}>
            {deal.AccountName}
          </h3>
          <p className="text-sm text-gray-600 truncate" title={deal.ServiceType}>
            {deal.ServiceType}
          </p>
        </div>
        <div className="flex flex-col items-end space-y-1">
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${divisionConfig.badgeColor}`}>
            {deal.Division}
          </span>
        </div>
      </div>

      {/* Deal Value */}
      <div className="mb-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">Estimated Annual Deal Value:</span>
          <span className="text-lg font-semibold text-green-600">
            {formatIndianCurrency(deal.DealValue)}
          </span>
        </div>
      </div>

      {/* Deal Details */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">Salesperson:</span>
          <span className="text-gray-900 font-medium">{deal.SalespersonName}</span>
        </div>
        
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">Contact:</span>
          <span className="text-gray-900">{deal.ContactName}</span>
        </div>

        {deal.ExpectedClosureDate && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Expected Closure:</span>
            <span className="text-gray-900">
              {new Date(deal.ExpectedClosureDate).toLocaleDateString()}
            </span>
          </div>
        )}

        {deal.LeadSource && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Lead Source:</span>
            <span className="text-gray-900">{deal.LeadSource}</span>
          </div>
        )}

        {deal.LeadGeneratorName && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Lead Generated By:</span>
            <span className="text-gray-900">{deal.LeadGeneratorName}</span>
          </div>
        )}
      </div>

      {/* Notes */}
      {deal.Notes && (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-xs text-gray-500 mb-1">Notes:</p>
          <p className="text-sm text-gray-700 line-clamp-2">{deal.Notes}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={handleViewDetails}
          className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
        >
          View Details
        </button>
        <button
          onClick={handleReject}
          disabled={isApproving || isRejecting}
          className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isRejecting ? 'Rejecting...' : 'Reject'}
        </button>
        <button
          onClick={handleApprove}
          disabled={isApproving || isRejecting}
          className="flex-1 px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isApproving ? 'Approving...' : 'Approve'}
        </button>
      </div>
    </div>
  );
};

export default ApprovalCard;